import { map, of } from 'rxjs';
import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { scopedCollection } from '../server/scoped-collection.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-util/middlewares/checkAuthorizedMiddleware.js';
import { Permission } from '../enums/roles.js';
import { GLOBAL_SETTINGS_ID, Setting, getGlobalSettings } from '../entities/settings.js';

export const getNewProductSetting = registerQuery({
  operationName: 'getNewProductSetting',
  type: () => Setting,
  params: [{ name: 'institutionID', type: () => String }],
  middlewares: [
    checkAuthorizedMiddleware(
      ({ user, params }) =>
        user.hasPermission(Permission.EDIT_ADMIN_INSTITUTION_WORKSHOPS, params.institutionID) ||
        user.hasPermission(Permission.EDIT_ADMIN_INSTITUTION_COUNSELINGS, params.institutionID),
    ),
  ],
  resolve: (institutionID: string) => {
    return async (gqlFields: BaseQLSelectionSet<Setting>) => {
      // try to find unused settings object (to prevent creating lots of unused setting-objects)
      const settingCol = await scopedCollection(Setting._class);
      // TODO: solve this diffrently. Unused settings should not get created in the first place
      const unusedSettings = await settingCol.find({ parentInstitutionID: institutionID, productID: null }).toArray();
      if (unusedSettings.length > 0) return unusedSettings[0] as Setting;

      const newSetting = new Setting({ parentInstitutionID: institutionID });
      const id = await newSetting.create();
      return (await settingCol.findOne({ _id: id })) as Setting;
    };
  },
});

export const getProductSetting = registerQuery({
  operationName: 'getProductSetting',
  type: () => Setting,
  params: [
    { name: 'productID', type: () => String },
    { name: 'key', type: () => String },
  ],
  resolve: (productID: string, key: string) => {
    return async (gqlFields: BaseQLSelectionSet<Setting>) => {
      const productSetting = (await loadMongoData(Setting, { productID })).at(0);
      if (!productSetting) throw new Error('Settings not found for product: ' + productID);
      if (!productSetting[key] !== null && productSetting[key] !== undefined) return productSetting[key];
      if (!productSetting.parentInstitutionID)
        throw new Error('product Settings has no parent institution: ' + productSetting._id);
      const institutionSetting = (
        await loadMongoData(Setting, { institutionID: productSetting.parentInstitutionID })
      ).at(0);
      if (!institutionSetting)
        throw new Error('Settings not found for institution: ' + productSetting.parentInstitutionID);
      if (!institutionSetting[key] !== null && institutionSetting[key] !== undefined) return institutionSetting[key];

      const globalSetting = await getGlobalSettingsQuery()(gqlFields);
      return globalSetting[key];
    };
  },
});

const getGlobalSettingsQuery = registerQuery({
  operationName: 'getGlobalSettings',
  type: () => Setting,
  params: [],
  resolve: () => {
    return async <T extends BaseQLSelectionSet<Setting>>(gqlFields: T): Promise<NarrowedEntity<Setting, T>> => {
      const existingGlobalSettings = (await getGlobalSettings()) as Setting as NarrowedEntity<Setting, T>;
      if (existingGlobalSettings) return existingGlobalSettings;

      // create global Settings
      const newGlobalSettings = new Setting({ _id: GLOBAL_SETTINGS_ID });
      await newGlobalSettings.create();
      return newGlobalSettings as NarrowedEntity<Setting, T>;
    };
  },
});

export { getGlobalSettingsQuery as getGlobalSettings };

export const getSettingsByIDs = registerQuery({
  operationName: 'getSettingsByIDs',
  type: () => [Setting],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Setting>>(gqlFields: SelSet) =>
      readEntitiesByIDs(Setting, ids, gqlFields),
});

export const subscribeSettingsByIDs = registerSubscription({
  operationName: 'subscribeSettingsByIDs',
  type: () => [Setting],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Setting>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(Setting, ids, gqlFields),
});

export const getAllSettings = registerQuery({
  operationName: 'getAllSettings',
  type: () => [Setting],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Setting>>(gqlFields: SelSet) =>
      readAllEntities(Setting, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllSettings = registerSubscription({
  operationName: 'subscribeAllSettings',
  type: () => [Setting],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Setting>>(gqlFields: SelSet) =>
      watchAllEntities(Setting, gqlFields, { skip, limit, excludeIDs }),
});

export const getSettingByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<Setting>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getSettingsByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeSettingByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<Setting>>(gqlFields: SelSet) =>
    id ? subscribeSettingsByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
